//defs
import { JuspayClientAuthTokenData, JuspaySdkFeatureAvailable } from "@payment-client/interfaces/Juspay";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { None, Option, Some } from "@swiggy-private/js-fp";
import { PaymentMethod, PaymentMethodMeta, VALID_PAYMENT_METHODS } from "@swiggy-private/payment-types";

//internal-deps
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import { PaymentGroupDataDef } from "@swiggy-private/payment-ui";

//helpers
import { getJuspaySdkFeatureAvailability, resolveClientAuthTokenDataForMerchantId } from "./JuspayHelpers";
import { getPaymentMethodFromGroup, isCardGroup, isFoodcardGroup, isPreferredGroup } from "./PaymentHelper";
import { isBusinessLineDineout } from "./Misc";
import { isEmpty, isString } from "@swiggy-private/js-utils";

//constants
import { CARD_BIN_NUMBER_MAX_LIMIT } from "@payment-client/constants/Card";
import { ConfigContextValue } from "@swiggy-private/checkout-core";

export const isSdkFlowAvailablForCard = (
    isSdkAvailable: boolean,
    featuresAvailability?: JuspaySdkFeatureAvailable,
): boolean => {
    return getJuspaySdkFeatureAvailability(VALID_PAYMENT_GROUP_TYPES.CARD, isSdkAvailable, featuresAvailability);
};

export function getNewcardPaymentMethod(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): Option<PaymentMethod<PaymentMethodMeta>> {
    const cardPaymentGroup = paymentGroups.find((group) => isCardGroup(group));
    if (cardPaymentGroup == null) {
        return None;
    }
    return getPaymentMethodFromGroup(cardPaymentGroup, VALID_PAYMENT_METHODS.NEW_CARD);
}

export function getFoodcardPaymentGroup(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): Option<PaymentGroupDataDef> {
    // Case 1: check for Foodcard payment group
    const foodCardGroup = Some(paymentGroups.find(isFoodcardGroup));
    return foodCardGroup.orElse(() => {
        // Case2: If Foodcard group is not present,
        // then check if preferred payment group is present
        // AND Foodcard payment method is in preferred group
        const preferredGroup = Some(paymentGroups.find(isPreferredGroup));
        if (preferredGroup.isNone()) return None;

        const group = preferredGroup.unwrap();
        const isFoodcardPreferred = group.paymentMethods.some(
            (method) => method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.FOOD_CARD,
        );
        return isFoodcardPreferred ? preferredGroup : None;
    });
}

export function getCardBinFromCardNumber(cardNumber: string) {
    return cardNumber.replace(/[\s]/g, "").substring(0, CARD_BIN_NUMBER_MAX_LIMIT);
}

export function getCardBinFromExtraParams(extraParams: { [key: string]: string }): string | undefined {
    if (isString(extraParams.cardBin)) {
        return extraParams.cardBin;
    } else if (isString(extraParams.cardNumber)) {
        return getCardBinFromCardNumber(extraParams.cardNumber);
    }
}

export const getValidClientAuthToken = async (
    fetchJuspayClientAuthToken?: () => Promise<
        JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData> | void
    >,
    rawClientAuthTokenData?: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData>,
    merchantId?: string,
    defaultMerchantId?: string,
): Promise<string> => {
    if (typeof fetchJuspayClientAuthToken !== "function") {
        throw new Error("No valid function available to fetch the Juspay client auth token");
    }

    const clientAuthTokenData = resolveClientAuthTokenDataForMerchantId(
        rawClientAuthTokenData,
        merchantId ?? "",
        defaultMerchantId ?? "",
    );

    const isTokenExpired = !isEmpty(clientAuthTokenData)
        ? Date.now() > clientAuthTokenData.clientAuthTokenExpiry
        : false;

    if (isEmpty(clientAuthTokenData) || isTokenExpired) {
        const updatedRaw = await fetchJuspayClientAuthToken();
        const updatedTokenData = resolveClientAuthTokenDataForMerchantId(
            updatedRaw as JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData> | undefined,
            merchantId ?? "",
            defaultMerchantId ?? "",
        );
        if (updatedTokenData?.clientAuthToken) {
            return updatedTokenData.clientAuthToken;
        } else {
            throw new Error("Failed to fetch valid Juspay client auth token.");
        }
    }

    if (!isEmpty(clientAuthTokenData)) {
        return clientAuthTokenData.clientAuthToken;
    }

    throw new Error("No valid Juspay client auth token found.");
};

export const isCardRateLimitEnabled = (configContext: ConfigContextValue): boolean => {
    return isBusinessLineDineout() || Boolean(configContext?.enableCardRateLimit?.unwrapOr(false));
};
